import { ControllerRootOptions } from '../../controller-classes';
import { nearestSixteenth } from '../../helpers';
import { vertex2Data } from './vertex-2.data';
import { addCfn, distance2Cfn, move2OnAxisCfn, rotate2AxisCfn, subtractCfn } from './functions';
import { Vertex2Data, Vertex2DataIn } from './types';

export class Vertex2 {
	private _x: number;
	private _y: number;
	private _readOnly: boolean;

	public get x (): number {
		return this._x;
	}

	public set x (value: number) {
		this.checkReadOnly();
		this._x = value;
	}

	public get y (): number {
		return this._y;
	}

	public set y (value: number) {
		this.checkReadOnly();
		this._y = value;
	}

	constructor (options: ControllerRootOptions<Vertex2DataIn> = {}) {
		// super(BlueprintInstanceType.Point, options);

		const { dataIn = null } = options;
		const data = vertex2Data(dataIn);

		this._x = data.x ?? 0;
		this._y = data.y ?? 0;
		this._readOnly = data.readOnly ?? false;
	}

	/**
	 * Gets the read-only state of the vertex
	 * @returns true if the vertex is read-only, false otherwise
	 */
	public get readOnly (): boolean {
		return this._readOnly;
	}

	/**
	 * Sets the read-only state of the vertex
	 * @param value true to make read-only, false to allow updates
	 */
	public set readOnly (value: boolean) {
		this._readOnly = value;
	}

	/**
	 * Throws an error if the vertex is read-only
	 * @private
	 */
	private checkReadOnly (): void {
		if (this._readOnly) {
			throw new Error('Cannot modify read-only vertex');
		}
	}

	/**
	 * inverts x, y and z values
	 * @returns This Point
	 */
	public invert (): Vertex2 {
		this.checkReadOnly();
		this._x *= -1;
		this._y *= -1;

		return this;
	}

	public moveTo (point: Vertex2): Vertex2 {
		this.checkReadOnly();
		this._x = point.x;
		this._y = point.y;

		return this;
	}

	public roundNearestSixteenth (): Vertex2 {
		this.checkReadOnly();
		this._x = nearestSixteenth(this._x);
		this._y = nearestSixteenth(this._y);

		return this as Vertex2;
	}

	public clone (): Vertex2 {
		return new Vertex2({ dataIn: this.serialize() })
	}

	public serialize (): Vertex2Data {
		return {
			x: this._x,
			y: this._y
		};
	}

	public add (distance: Vertex2): Vertex2 { return addCfn(this, distance); }
	public moveOnAxis (distance: number, angle: number): Vertex2 { return move2OnAxisCfn(this, distance, angle); }
	public subtract (distance: Vertex2): Vertex2 { return subtractCfn(this, distance); }
	public rotateAxis (origin: Vertex2, angle: number): Vertex2 { return rotate2AxisCfn(this, origin, angle); }

	public getDistance (point: Vertex2): number { return distance2Cfn(this, point); }
}
